/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {moveItemInArray} from '@angular/cdk/drag-drop';
import {NestedTreeControl} from '@angular/cdk/tree';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTreeNestedDataSource} from '@angular/material/tree';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {forkJoin} from 'rxjs';
import {DISMISS_MSG} from '../../../common/constants';
import {StateService} from '../../../core/state/state.service';
import {Segment} from '../../../models/segment.model';
import {User} from '../../../models/user.model';
import {SegmentSchemeMapService} from '../../../services/segment-scheme-map.service';
import {SegmentService} from '../../../services/segment.service';
import {AddSegmentPopupComponent} from './add-segment-popup/add-segment-popup.component';

@Component({
    selector: 'app-segment-scheme-management',
    templateUrl: './segment-scheme-management.component.html',
    styleUrls: ['./segment-scheme-management.component.scss'],
    standalone: false
})
export class SegmentSchemeManagementComponent implements OnInit {

  unmappedSegmentsTreeControl = new NestedTreeControl<Segment>(node => node.children);
  unmappedSegmentsDatasource = new MatTreeNestedDataSource<Segment>();

  mappedSegmentsTreeControl = new NestedTreeControl<Segment>(node => node.children);
  mappedSegmentsDatasource = new MatTreeNestedDataSource<Segment>();

  selectedSegment: Segment = null;
  showSpinner: boolean;
  childSpinner: boolean;
  user: User;

  constructor(private readonly segmentService: SegmentService,
              private readonly segmentSchemeMapService: SegmentSchemeMapService,
              private readonly userService: StateService,
              public readonly dialog: MatDialog,
              public snackbar: FabMessageService) {
  }

  ngOnInit(): void {
    this.loadSegmentDatasources();
    this.user = this.userService.user;
    this.childSpinner = false;
  }

  /**
   * Fetch mapped and unmapped segments. Utilizes ForkJoin to change the state of 'showSpinner' boolean
   * when both calls resolve.
   */
  loadSegmentDatasources(): void {
    this.showSpinner = true;
    const unmappedSegmentsObservable = this.segmentService.getNonMappedSegments();
    const mappedSegmentsObservable = this.segmentService.getMappedSegments();
    forkJoin([unmappedSegmentsObservable, mappedSegmentsObservable]).subscribe(
      data => {
        // Ignore level one segments without children (legacy segments)
        this.unmappedSegmentsDatasource.data = data[0].filter(value => value.children.length > 0);
        this.mappedSegmentsDatasource.data = data[1];
      },
      err => {
        this.snackbar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      },
      () => {
        this.showSpinner = false;
      }
    );
  }

  /**
   * Select or unselect the segment the user clicked.
   * @param segment Segment passed in from the template
   */
  selectSegment(segment: Segment): void {
    if (this.selectedSegment === segment) {
      this.selectedSegment = null;
    } else {
      this.selectedSegment = segment;
    }
  }

  /**
   * Enables the user to move the selected, mapped segment up the segment order.
   */
  enableSegmentOrderUp(): boolean {
    return this.selectedSegment &&
      this.mappedSegmentsDatasource.data.indexOf(this.selectedSegment) !== -1 &&
      this.mappedSegmentsDatasource.data.indexOf(this.selectedSegment) !== 0;
  }

  /**
   * Moves the selected, mapped segment up the segment order.
   */
  moveSegmentUp(): void {
    if (this.enableSegmentOrderUp()) {
      this.showSpinner = true;
      const selectedIndex = this.mappedSegmentsDatasource.data.indexOf(this.selectedSegment);
      const segmentOne = this.selectedSegment;
      const segmentTwo = this.mappedSegmentsDatasource.data[selectedIndex - 1];

      // Swap segment orders of the affected segments
      let updatedSegments = this.swapSegmentOrders(segmentOne, segmentTwo);
      // Update the segments with the User's GMID
      updatedSegments = this.updateSegmentArrayGmid(updatedSegments);

      this.segmentSchemeMapService.updateMapping(updatedSegments).subscribe({
        error: err => {
          this.snackbar.displayError(err, DISMISS_MSG);
          this.loadSegmentDatasources();
        },
        complete: () => {
          moveItemInArray(this.mappedSegmentsDatasource.data, selectedIndex, selectedIndex - 1);
          this.refreshDatasources();
        }
      });
    }
  }

  /**
   * Enables the user to move the selected, mapped segment down the segment order.
   */
  enableSegmentOrderDown(): boolean {
    return this.selectedSegment &&
      this.mappedSegmentsDatasource.data.indexOf(this.selectedSegment) !== -1 &&
      this.mappedSegmentsDatasource.data.indexOf(this.selectedSegment) !== this.mappedSegmentsDatasource.data.length - 1;
  }

  /**
   * Moves the selected, mapped segment down the segment order.
   * Calls the segment service to swap the segment order of the selected segment, as well as the segment below it.
   */
  moveSegmentDown(): void {
    if (this.enableSegmentOrderDown()) {
      this.showSpinner = true;
      const selectedIndex = this.mappedSegmentsDatasource.data.indexOf(this.selectedSegment);
      const segmentOne = this.selectedSegment;
      const segmentTwo = this.mappedSegmentsDatasource.data[selectedIndex + 1];

      // Swap segment orders of the affected segments
      let updatedSegments = this.swapSegmentOrders(segmentOne, segmentTwo);
      // Update the segments with the User's GMID
      updatedSegments = this.updateSegmentArrayGmid(updatedSegments);

      this.segmentSchemeMapService.updateMapping(updatedSegments).subscribe({
        error: err => {
          this.snackbar.displayError(err, DISMISS_MSG);
          this.loadSegmentDatasources();
        },
        complete: () => {
          moveItemInArray(this.mappedSegmentsDatasource.data, selectedIndex, selectedIndex + 1);
          this.refreshDatasources();
        }
      });
    }
  }

  /**
   * Takes in two Segment objects and swaps their segment orders. Cascades down to children.
   * @param segmentOne The selected segment. Contains at least one level of children segments.
   * @param segmentTwo The adjacent segment. Contains at least one level of children segments.
   */
  swapSegmentOrders(segmentOne: Segment, segmentTwo: Segment): Segment[] {
    const segmentOneSegmentOrder = segmentOne.segmentOrder;
    const segmentTwoSegmentOrder = segmentTwo.segmentOrder;
    segmentOne = this.updateSegmentOrder(segmentOne, segmentTwoSegmentOrder);
    segmentTwo = this.updateSegmentOrder(segmentTwo, segmentOneSegmentOrder);
    return [segmentOne, segmentTwo];
  }

  /**
   * Recursively updates a Segment's segment order.
   * @param segment Segment to update.
   * @param value The value to set the segment's segment order to.
   */
  updateSegmentOrder(segment: Segment, value: number): Segment {
    segment.segmentOrder = value;
    for (const childSegment of segment.children) {
      this.updateSegmentOrder(childSegment, value);
    }
    return segment;
  }

  /**
   * Update a Segment array with the user's GMID
   * @param segments The segment objects to update
   */
  updateSegmentArrayGmid(segments: Segment[]): Segment[] {
    for (const segment of segments) {
      this.updateSegmentGmid(segment);
    }
    return segments;
  }

  /**
   * Recursively updates the given Segment's updBy field.
   * @param segment Segment to be updated.
   */
  updateSegmentGmid(segment: Segment): void {
    segment.updBy = this.user.gmid;
    for (const childSegment of segment.children) {
      this.updateSegmentGmid(childSegment);
    }
  }

  /**
   * If user has an unmapped segment selected, enable the 'Move to Scheme' button.
   */
  enableMoveSegmentToScheme(): boolean {
    return this.selectedSegment && this.unmappedSegmentsDatasource.data.indexOf(this.selectedSegment) !== -1;
  }

  /**
   * Moves the selected unmapped segment to the Global Scheme.
   */
  moveSegmentToScheme(): void {
    if (this.enableMoveSegmentToScheme()) {
      this.showSpinner = true;
      // Setting the segment order for the new segment - by default segment is put at end of list
      this.setSegmentOrders(this.selectedSegment);
      this.updateSegmentGmid(this.selectedSegment);
      this.segmentSchemeMapService.createSegmentSchemeMap(this.selectedSegment).subscribe({
        error: err => {
          this.snackbar.displayError(err, DISMISS_MSG);
          this.loadSegmentDatasources();
        },
        complete: () => {
          this.snackbar.displaySuccess('Added segment to GM Global Scheme', DISMISS_MSG);
          this.loadSegmentDatasources();
        }
      });
    }
  }

  /**
   * Recursively sets the segment order for a created segment. Created segments are, by default, set to the end
   * of the mapped segment list.
   * @param segment Segment to update.
   */
  setSegmentOrders(segment: Segment): void {
    segment.segmentOrder = this.mappedSegmentsDatasource.data.length + 1;
    for (const childSegment of segment.children) {
      this.setSegmentOrders(childSegment);
    }
  }

  /**
   * If user has a mapped segment selected, enable the 'Move from Scheme' button.
   */
  enableMoveSegmentFromScheme(): boolean {
    return this.selectedSegment && this.mappedSegmentsDatasource.data.indexOf(this.selectedSegment) !== -1;
  }

  /**
   * Moves the selected mapped segment from the Global Scheme.
   */
  deleteSchemeMapping(): void {
    if (this.enableMoveSegmentFromScheme()) {
      this.showSpinner = true;
      this.segmentSchemeMapService.deleteSegmentSchemeMap(this.selectedSegment).subscribe({
        error: err => {
          this.snackbar.displayError(err, DISMISS_MSG);
          this.showSpinner = false;
        },
        complete: () => {
          const index = this.mappedSegmentsDatasource.data.indexOf(this.selectedSegment);
          // If segment is not the last in the list, update segment orders that fall after deleted segment
          if (index + 1 !== this.mappedSegmentsDatasource.data.length) {
            this.updateSubsequentSegmentOrders(index);
            this.updateSegmentArrayGmid(this.mappedSegmentsDatasource.data.slice(index + 1));
            this.segmentSchemeMapService.updateMapping(this.mappedSegmentsDatasource.data.slice(index + 1)).subscribe({
              error: err => {
                this.snackbar.displayError(err, DISMISS_MSG);
                this.loadSegmentDatasources();
              },
              complete: () => {
                this.snackbar.displaySuccess('Removed segment from GM Global Scheme', DISMISS_MSG);
                this.loadSegmentDatasources();
              }
            });
          } else {
            // If no segment orders are affected, datasource still needs to be refreshed
            this.snackbar.displaySuccess('Removed segment from GM Global Scheme', DISMISS_MSG);
            this.loadSegmentDatasources();
          }
        }
      });
    }
  }

  /**
   * checks the selected segment to see if it is in use in industry scenarios
   * If user selects confirm, it removes the segment from global scheme
   */
  moveSegmentFromScheme(): void {
    this.segmentSchemeMapService.getScenarioSegmentDetails(this.selectedSegment.mappingId).subscribe(
      data => {
        if (data.length === 0) {
          this.deleteSchemeMapping();
        } else {
          this.dialog.open(FabConfirmationDialogComponent, {
            width: '400px',
            data: {
              title: 'Confirmation',
              message: 'The Segment is in use by Industry Scenario(s). Proceeding will assign volume ' +
                'associated to the segment to segment value ND. Do you want to proceed?',
              noLabel: 'Cancel',
              yesLabel: 'Confirm',
              titleIcon: 'warning',
              actionColor: 'warn'
            },
            disableClose: true
          })
          .afterClosed().subscribe({
            next: response => {
              if (response) {
                this.dialog.open(FabConfirmationDialogComponent, {
                  width: '400px',
                  data: {
                    title: 'Confirmation',
                    message: 'This is a non-revertable change and will assign volume associated to the segment to ' +
                      'the segment value ND. Do you still want to proceed?',
                    noLabel: 'Cancel',
                    yesLabel: 'Confirm',
                    titleIcon: 'warning',
                    actionColor: 'warn'
                  },
                  disableClose: true
                })
                .afterClosed().subscribe({
                  next: confirmation => {
                    if (confirmation) {
                      this.deleteSchemeMapping();
                    }
                  }
                });
              }
            }
          });
        }
      },
      err => {
        this.snackbar.displayError(err.error.message, DISMISS_MSG);
      }
    );
  }

  /**
   * Updates segment orders after deleting a mapping. If deleted segment is the last element, this method is not called.
   * @param index Starting index for segments to update.
   */
  updateSubsequentSegmentOrders(index: number): void {
    for (let i = index + 1; i < this.mappedSegmentsDatasource.data.length; i++) {
      this.mappedSegmentsDatasource.data[i] = this.incrementSegmentOrder(this.mappedSegmentsDatasource.data[i]);
    }
  }

  /**
   * Recursively subtracts 1 from the subsequent segments after deleting a mapping.
   * @param segment Segment passed in from the MappedSegmentsDatasource
   */
  incrementSegmentOrder(segment: Segment): Segment {
    segment.segmentOrder = segment.segmentOrder - 1;
    for (const childSegment of segment.children) {
      this.incrementSegmentOrder(childSegment);
    }
    return segment;
  }

  /**
   * Opens dialog for user to add a new segment. Level 1 is user input, level 3 is auto-generated EV/ICE breakdown.
   */
  openSegmentCreationDialog(): void {
    this.dialog.open(AddSegmentPopupComponent)
    .afterClosed().subscribe({
      complete: () => {
        this.loadSegmentDatasources();
      }
    });
  }

  /**
   * Deletes the selected, unmapped segment.
   */
  deleteSegment(segment: Segment): void {
    this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmation',
        message: 'Selected segment and its subsegments will be deleted. Are you sure?',
        noLabel: 'Cancel',
        yesLabel: 'Confirm',
        titleIcon: 'warning',
        actionColor: 'warn'
      },
      disableClose: true
    })
    .afterClosed().subscribe({
      next: confirm => {
        if (confirm) {
          this.showSpinner = true;
          this.segmentService.deleteSegment(segment).subscribe({
            error: err => {
              this.snackbar.displayError(err, DISMISS_MSG);
              this.loadSegmentDatasources();
            },
            complete: () => {
              this.snackbar.displaySuccess('Successfully deleted segment', DISMISS_MSG);
              this.loadSegmentDatasources();
            }
          });
        }
      }
    });
  }


  /**
   * Refresh both the mapped and unmapped datasources. WIP, need to find a better way.
   */
  refreshDatasources(): void {
    //eslint-disable-next-line
    this.unmappedSegmentsDatasource.data = this.unmappedSegmentsDatasource.data;
    //eslint-disable-next-line
    this.mappedSegmentsDatasource.data = this.mappedSegmentsDatasource.data;
    this.showSpinner = false;
  }

  /**
   * Determines if the segment has children - used for generating tree structure.
   */
  hasChild = (_: number, node: Segment) => !!node.children && node.children.length > 0;

  /**
   * Downloads the Segment Scheme Report excel to the user
   */
  downloadReport(): void {
    this.segmentSchemeMapService.getReport();
  }
}
